/**
 * The files of a local upload as one uncompressed POSIX tar (pax), the body the server reads
 * with Python's `tarfile` (`docs/components/content.md` D-18). File contents stay Blobs, so
 * the browser streams them instead of reading them into memory.
 */

export interface UploadFile {
  /** The path inside the upload, `/`-separated, such as `letters/1520.txt`. */
  readonly path: string;
  readonly content: Blob;
}

const BLOCK = 512;
const USTAR_NAME_BYTES = 100;
const encoder = new TextEncoder();

export function uploadArchive(files: readonly UploadFile[]): Blob {
  const parts: BlobPart[] = [];
  files.forEach((file, index) => {
    const name = encoder.encode(file.path);
    let headerName = file.path;
    if (name.length > USTAR_NAME_BYTES || name.some((byte) => byte > 0x7f)) {
      // A pax record carries the full UTF-8 path; the ustar name is only a placeholder.
      const record = paxRecord("path", file.path);
      parts.push(header(`PaxHeader/${String(index)}`, record.length, "x"), record, padding(record.length));
      headerName = `file-${String(index)}`;
    }
    parts.push(header(headerName, file.content.size, "0"), file.content, padding(file.content.size));
  });
  parts.push(new Uint8Array(2 * BLOCK));
  return new Blob(parts, { type: "application/x-tar" });
}

/** One `"<length> <key>=<value>\n"` record, whose length counts its own digits. */
function paxRecord(key: string, value: string): Uint8Array<ArrayBuffer> {
  const body = encoder.encode(` ${key}=${value}\n`);
  let length = body.length + 1;
  while (String(length).length + body.length !== length) length = String(length).length + body.length;
  return encoder.encode(`${String(length)} ${key}=${value}\n`);
}

function header(name: string, size: number, type: "0" | "x"): Uint8Array<ArrayBuffer> {
  const block = new Uint8Array(BLOCK);
  const write = (offset: number, text: string) => { block.set(encoder.encode(text), offset); };
  write(0, name);
  write(100, "0000644\0");
  write(108, "0000000\0");
  write(116, "0000000\0");
  write(124, `${size.toString(8).padStart(11, "0")}\0`);
  write(136, "00000000000\0");
  write(148, "        ");
  write(156, type);
  write(257, "ustar\0");
  write(263, "00");
  const checksum = block.reduce((sum, byte) => sum + byte, 0);
  write(148, `${checksum.toString(8).padStart(6, "0")}\0 `);
  return block;
}

function padding(size: number): Uint8Array<ArrayBuffer> {
  return new Uint8Array((BLOCK - (size % BLOCK)) % BLOCK);
}
